import React, { useEffect, useState } from 'react';
import Icon from '../../../components/common/Icon/Icon';
import type { CheckoutFormState, CheckoutPaymentInstructions, CheckoutSelectedPlan } from '../checkout.types';
import { CHECKOUT_PIX_POLL_INTERVAL_MS } from '../checkout.constants';
import { CheckoutPaymentStatusService } from '../../../services/CheckoutPaymentStatusService';
import { LANDPAGE_HERO } from '../../Landpage/landpage.constants';
import CheckoutPaymentInstructionsPanel from './CheckoutPaymentInstructionsPanel';
import OrderSummary from './OrderSummary';
import { resolveCheckoutPayerForSubmission } from '../checkout.utils';
import styles from './CheckoutSuccessView.module.css';

const CREDIT_CARD_CONFIRMED_MESSAGE =
  'Pagamento no cartão confirmado! Sua contratação foi concluída com sucesso.';

const CHARGE_CREDIT_CARD_CONFIRMED_MESSAGE = 'Pagamento no cartão confirmado. Obrigada!';

interface CheckoutSuccessViewProps {
  form: CheckoutFormState;
  selectedPlan: CheckoutSelectedPlan;
  message: string;
  paymentInstructions?: CheckoutPaymentInstructions | null;
  externalPaymentId?: string | null;
  mode?: 'checkout' | 'charge_checkout';
}

const CheckoutSuccessView: React.FC<CheckoutSuccessViewProps> = ({
  form,
  selectedPlan,
  message,
  paymentInstructions = null,
  externalPaymentId = null,
  mode = 'checkout',
}) => {
  const isChargeCheckout = mode === 'charge_checkout';
  const creditCardPaymentId = form.paymentMethod === 'credit_card'
    ? (externalPaymentId || '').trim()
    : '';
  const isCreditCardCheckout = form.paymentMethod === 'credit_card' && !paymentInstructions;
  const [isCreditCardPaid, setIsCreditCardPaid] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const isCreditCardProcessing = isCreditCardCheckout && !isCreditCardPaid;

  useEffect(() => {
    if (!isCreditCardCheckout || !creditCardPaymentId || isCreditCardPaid) {
      return undefined;
    }

    let cancelled = false;

    const pollPaymentStatus = async () => {
      try {
        const paid = await CheckoutPaymentStatusService.isPaymentPaid(creditCardPaymentId);

        if (!cancelled && paid) {
          setIsCreditCardPaid(true);
          setPollError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setPollError(
            error instanceof Error
              ? error.message
              : 'Não foi possível verificar o status do pagamento.',
          );
        }
      }
    };

    void pollPaymentStatus();
    const intervalId = window.setInterval(() => {
      void pollPaymentStatus();
    }, CHECKOUT_PIX_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [creditCardPaymentId, isCreditCardCheckout, isCreditCardPaid]);

  const title = isChargeCheckout
    ? 'Obrigada pelo pagamento!'
    : 'Contratação realizada com sucesso!';

  const displayMessage = isCreditCardProcessing
    ? null
    : isCreditCardCheckout
      ? (isChargeCheckout ? CHARGE_CREDIT_CARD_CONFIRMED_MESSAGE : CREDIT_CARD_CONFIRMED_MESSAGE)
      : message;

  const hint = paymentInstructions
    ? (isChargeCheckout
      ? 'Finalize o pagamento abaixo para concluir.'
      : 'Finalize o pagamento abaixo para ativar sua contratação.')
    : isCreditCardProcessing
      ? (isChargeCheckout
        ? 'Pode fechar esta página com tranquilidade. Assim que o pagamento for confirmado, atualizamos tudo automaticamente.'
        : 'Você já pode fechar esta página com tranquilidade. Assim que o pagamento for confirmado, atualizaremos tudo automaticamente.')
      : (isChargeCheckout
        ? 'Se precisar de ajuda, fale com o suporte.'
        : 'Confira abaixo o resumo dos dados informados e do plano contratado.');

  return (
    <article className={styles.successCard}>
      <div className={styles.successHeader}>
        <span
          className={`${styles.successIconWrap} ${isCreditCardProcessing ? styles.successIconLoading : ''}`.trim()}
          aria-hidden
        >
          <Icon name="check_circle" size={40} filled color="var(--color-success)" />
        </span>
        <h2 className={styles.successTitle}>{title}</h2>
        {isCreditCardProcessing ? (
          <div className={styles.successMessageGroup}>
            {isChargeCheckout ? (
              <>
                <p className={styles.successMessage}>
                  Obrigada! Recebemos o seu pagamento e ele está sendo processado pela operadora.
                  Isso pode levar alguns minutos.
                </p>
                <p className={styles.successMessage}>
                  Não é necessário permanecer nesta página. Pode fechá-la e aguardar a confirmação
                  com tranquilidade.
                </p>
              </>
            ) : (
              <>
                <p className={styles.successMessage}>
                  Sua contratação foi registrada com sucesso. O pagamento no cartão ainda está sendo
                  processado pela operadora e pode levar alguns minutos ou até algumas horas.
                </p>
                <p className={styles.successMessage}>
                  Fique tranquila: não é necessário permanecer nesta página. Você pode fechá-la e
                  aguardar a confirmação com tranquilidade.
                </p>
              </>
            )}
          </div>
        ) : (
          <p className={styles.successMessage}>{displayMessage}</p>
        )}
        <p className={styles.successHint}>{hint}</p>
        {pollError && (
          <p className={styles.pollError} role="alert">
            {pollError}
          </p>
        )}
        <div className={styles.supportBlock}>
          <p className={styles.supportHint}>
            Em caso de dúvidas, entre em contato com o suporte:
          </p>
          <a
            className={styles.supportButton}
            href={LANDPAGE_HERO.supportWhatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg
              className={styles.supportIcon}
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="currentColor"
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
              />
            </svg>
            Suporte
          </a>
        </div>
      </div>

      {paymentInstructions && (
        <CheckoutPaymentInstructionsPanel instructions={paymentInstructions} />
      )}

      <OrderSummary
        personal={form.personal}
        address={form.address}
        selectedPlan={selectedPlan}
        paymentMethod={form.paymentMethod}
        discountCents={form.discountCents}
        card={form.paymentMethod === 'credit_card' ? form.card : undefined}
        payer={form.paymentMethod === 'credit_card' ? resolveCheckoutPayerForSubmission(form) : null}
        useStudentDataAsPayer={form.useStudentDataAsPayer}
        showPlanDetails
        compact={isChargeCheckout}
      />
    </article>
  );
};

export default CheckoutSuccessView;
